package com.zq.webproject.ui.home

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.zq.webproject.model.Product
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import com.zq.webproject.network.Repository
import com.zq.webproject.network.model.Banner
import com.zq.webproject.network.model.Category
import com.zq.webproject.Platform
import com.zq.webproject.getPlatform
import com.zq.webproject.utils.TokenManager
import com.zq.webproject.utils.UrlLauncher
import com.zq.webproject.utils.getNetworkMonitor
import kotlinx.coroutines.delay

/**
 * 首页ViewModel
 * 管理首页的数据加载和状态
 */
class HomeViewModel {
    private val repository = Repository()
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    // UI状态
    var uiState by mutableStateOf(HomeUiState())
        private set

    // 当前选中的分类ID
    var selectedCategoryId by mutableStateOf<Int?>(null)
        private set

    // 登录状态
    var isLoggedIn by mutableStateOf(false)
        private set

    var currentToken by mutableStateOf<String?>(null)
        private set

    var currentUser by mutableStateOf<String?>(null)
        private set

    // 当前用户角色（user/affiliate/admin）
    var currentUserRole by mutableStateOf<String?>(null)
        private set

    // 显示登录对话框
    var showLoginDialog by mutableStateOf(false)
        private set

    // 显示我的页面
    var showProfileScreen by mutableStateOf(false)
        private set

    // 显示商品详情页面
    var showProductDetail by mutableStateOf(false)
        private set

    // 当前查看的商品ID
    var currentProductId by mutableStateOf<String?>(null)
        private set

    // 显示订单详情页面
    var showOrderDetail by mutableStateOf(false)
        private set

    // 当前查看的订单号
    var currentOrderNo by mutableStateOf<String?>(null)
        private set

    // 显示支付成功页面
    var showPaymentSuccess by mutableStateOf(false)
        private set

    // 支付成功的订单号
    var paymentSuccessOrderNo by mutableStateOf<String?>(null)
        private set

    // 支付成功的金额
    var paymentSuccessAmount by mutableStateOf<Double?>(null)
        private set

    // 支付成功的支付方式
    var paymentSuccessPaymentMethod by mutableStateOf<String?>(null)
        private set

    // 支付成功的资源链接（网盘地址）
    var paymentSuccessResourceUrl by mutableStateOf<String?>(null)
        private set

    // 显示邀请页面
    var showInviteScreen by mutableStateOf(false)
        private set

    // 显示邀请用户列表页面
    var showInvitedUsersScreen by mutableStateOf(false)
        private set

    // 显示购买记录页面
    var showPurchaseRecordScreen by mutableStateOf(false)
        private set

    // 显示提现页面
    var showWithdrawScreen by mutableStateOf(false)
        private set

    // 显示充值页面
    var showRechargeScreen by mutableStateOf(false)
        private set

    // 显示添加银行卡页面
    var showAddBankCardScreen by mutableStateOf(false)
        private set

    // 显示提现记录页面
    var showWithdrawRecordScreen by mutableStateOf(false)
        private set

    // 显示收益记录页面
    var showEarningsRecordScreen by mutableStateOf(false)
        private set

    // 显示排行榜页面
    var showRankingScreen by mutableStateOf(false)
        private set

    // 显示关于页面
    var showAboutScreen by mutableStateOf(false)
        private set

    // 显示上传商品页面
    var showUploadProductScreen by mutableStateOf(false)
        private set

    // 显示升级管理员弹框
    var showUpgradeAdminDialog by mutableStateOf(false)
        private set
    
    // 显示升级管理员支付二维码弹框
    var showUpgradeAdminQrcodeDialog by mutableStateOf(false)
        private set
    
    // 升级管理员支付二维码URL
    var upgradeAdminQrcodeUrl by mutableStateOf<String?>(null)
        private set
    
    // 升级管理员订单号
    var upgradeAdminOrderNo by mutableStateOf<String?>(null)
        private set

    // 下拉刷新状态
    var isRefreshing by mutableStateOf(false)
        private set

    // 上拉加载更多状态
    var isLoadingMore by mutableStateOf(false)
        private set

    // 当前页码
    var currentPage by mutableStateOf(1)
        private set

    // 是否还有更多数据
    var hasMore by mutableStateOf(true)
        private set

    // 网络监听器
    private val networkMonitor = getNetworkMonitor()
    
    // 网络状态
    var isNetworkAvailable by mutableStateOf(true)
        private set
    
    // 显示网络状态提示
    var showNetworkDialog by mutableStateOf(false)
        private set
    
    // 网络状态提示信息
    var networkMessage by mutableStateOf("")
        private set

    // 资源信息相关状态
    private val _resourceProduct = mutableStateOf<Product?>(null)
    val resourceProduct: androidx.compose.runtime.State<Product?> = _resourceProduct

    // 显示资源信息页面
    var showResourceInfo by mutableStateOf(false)
        private set


    init {
        println("HomeViewModel - 初始化开始") // 日志输出
        
        // 设置 Token 失效回调，当 Token 失效时自动退出登录
        TokenManager.setOnTokenInvalidCallback {
            println("HomeViewModel - Token失效回调触发，自动退出登录")
            // 清除登录状态
            isLoggedIn = false
            currentToken = null
            currentUser = null
            // 关闭所有页面，返回首页
            showProfileScreen = false
            showProductDetail = false
            showOrderDetail = false
            showPaymentSuccess = false
            showInviteScreen = false
            showInvitedUsersScreen = false
            showPurchaseRecordScreen = false
            showWithdrawScreen = false
            showRechargeScreen = false
            showAddBankCardScreen = false
            showWithdrawRecordScreen = false
            showEarningsRecordScreen = false
            showRankingScreen = false
            // 显示登录对话框
            showLoginDialog = true
        }
        
        // 启动网络监听
        networkMonitor.startMonitoring()
        
        // 监听网络状态变化
        scope.launch {
            networkMonitor.isNetworkAvailable.collect { isAvailable ->
                val wasAvailable = isNetworkAvailable
                isNetworkAvailable = isAvailable
                
                println("HomeViewModel - 网络状态变化: $wasAvailable -> $isAvailable")
                
                if (isAvailable && !wasAvailable) {
                    // 从断网恢复到联网
                    println("HomeViewModel - 网络已连接，刷新首页数据")
                    networkMessage = "网络已连接"
                    showNetworkDialog = true
                    
                    // 延迟关闭提示
                    scope.launch {
                        delay(2000)
                        showNetworkDialog = false
                    }
                    
                    // 刷新首页数据
                    refresh()
                } else if (!isAvailable && wasAvailable) {
                    // 从联网变为断网
                    println("HomeViewModel - 网络已断开")
                    networkMessage = "网络已断开，请检查网络连接"
                    showNetworkDialog = true
                }
            }
        }

        // 检查是否已登录
        if (TokenManager.isLoggedIn()) {
            val token = TokenManager.getToken()
            val username = TokenManager.getUsername()
            println("HomeViewModel - 检测到本地Token，自动登录: $username") // 日志输出

            isLoggedIn = true
            currentToken = token
            currentUser = username
            
            // 获取用户角色
            if (token != null) {
                getUserRole(token)
            }
        }

        // 无论是否登录，都加载首页数据（不需要Token）
        println("HomeViewModel - 加载首页数据（无需登录）") // 日志输出
        loadHomeData()
    }

    /**
     * 显示登录对话框
     */
    fun showLogin() {
        println("HomeViewModel - 显示登录对话框") // 日志输出
        showLoginDialog = true
    }

    /**
     * 隐藏登录对话框
     */
    fun hideLogin() {
        println("HomeViewModel - 隐藏登录对话框") // 日志输出
        showLoginDialog = false
        // 清空错误信息
        uiState = uiState.copy(error = null)
    }

    /**
     * 打开我的页面(已登录)或显示登录框(未登录)
     */
    fun openProfile() {
        if (isLoggedIn) {
            println("HomeViewModel - 已登录，打开我的页面") // 日志输出
            showProfileScreen = true
        } else {
            println("HomeViewModel - 未登录，显示登录对话框") // 日志输出
            showLoginDialog = true
        }
    }

    /**
     * 关闭我的页面
     */
    fun closeProfile() {
        println("HomeViewModel - 关闭我的页面") // 日志输出
        showProfileScreen = false
    }

    /**
     * 打开商品详情页面
     */
    fun openProductDetail(productId: Int) {
        println("HomeViewModel - 打开商品详情页面: productId=$productId") // 日志输出
        currentProductId = productId.toString()
        showProductDetail = true
    }

    /**
     * 关闭商品详情页面
     */
    fun closeProductDetail() {
        println("HomeViewModel - 关闭商品详情页面") // 日志输出
        showProductDetail = false
        currentProductId = null
    }

    /**
     * 打开订单详情页面
     */
    fun openOrderDetail(orderNo: String) {
        println("HomeViewModel - 打开订单详情页面: orderNo=$orderNo") // 日志输出
        currentOrderNo = orderNo
        showOrderDetail = true
    }

    /**
     * 关闭订单详情页面
     */
    fun closeOrderDetail() {
        println("HomeViewModel - 关闭订单详情页面") // 日志输出
        showOrderDetail = false
        currentOrderNo = null
    }

    /**
     * 退出登录
     */
    fun logout() {
        println("HomeViewModel - 用户退出登录") // 日志输出

        // 清除Token管理器中的数据
        TokenManager.clearToken()

        // 清除ViewModel中的状态
        isLoggedIn = false
        currentToken = null
        currentUser = null
        currentUserRole = null  // 清除用户角色

        // 关闭我的页面，返回首页
        showProfileScreen = false

        println("HomeViewModel - 退出登录完成，已返回首页") // 日志输出
    }
    
    /**
     * 获取用户角色
     */
    private fun getUserRole(token: String) {
        scope.launch {
            try {
                println("HomeViewModel - 开始获取用户角色")
                val result = repository.getUserRole(token)
                
                if (result.isSuccess) {
                    val response = result.getOrNull()
                    if (response?.code == 200 && response.data != null) {
                        val role = response.data.role
                        currentUserRole = role
                        println("HomeViewModel - 用户角色: $role")
                    } else {
                        println("HomeViewModel - 获取用户角色失败: ${response?.message}")
                    }
                } else {
                    println("HomeViewModel - 获取用户角色异常: ${result.exceptionOrNull()?.message}")
                }
            } catch (e: Exception) {
                println("HomeViewModel - 获取用户角色异常: ${e.message}")
                e.printStackTrace()
            }
        }
    }
    
    /**
     * 刷新首页数据（返回首页时调用）
     * 重新加载商品列表和用户角色信息
     */
    fun refreshHomeData() {
        println("HomeViewModel - 刷新首页数据")
        
        // 重新加载商品列表（根据当前选中的分类）
        currentPage = 1
        hasMore = true
        
        if (selectedCategoryId == null) {
            // 全部商品
            loadProducts(categoryId = null, page = "1", pageSize = "10")
        } else {
            // 特定分类的商品
            loadProducts(categoryId = selectedCategoryId.toString(), page = "1", pageSize = "10")
        }
        
        // 重新获取用户角色（检查是否已升级为管理员）
        val token = TokenManager.getToken()
        if (!token.isNullOrEmpty()) {
            getUserRole(token)
        }
    }
    
    /**
     * App 从后台切换到前台时调用
     * 检查用户角色是否变化（例如用户可能在其他设备升级了管理员）
     */
    fun onAppResumed() {
        println("HomeViewModel - App 从后台切换到前台，检查用户角色")
        
        // 如果用户已登录，重新获取用户角色
        if (isLoggedIn) {
            val token = TokenManager.getToken()
            if (!token.isNullOrEmpty()) {
                getUserRole(token)
            }
        }
    }
    
    /**
     * 打开邀请页面
     */
    fun openInvite() {
        println("HomeViewModel - 打开邀请页面") // 日志输出
        showInviteScreen = true
    }

    /**
     * 关闭邀请页面
     */
    fun closeInvite() {
        println("HomeViewModel - 关闭邀请页面") // 日志输出
        showInviteScreen = false
    }

    /**
     * 打开邀请用户列表页面
     */
    fun openInvitedUsers() {
        println("HomeViewModel - 打开邀请用户列表页面") // 日志输出
        showInvitedUsersScreen = true
    }

    /**
     * 关闭邀请用户列表页面
     */
    fun closeInvitedUsers() {
        println("HomeViewModel - 关闭邀请用户列表页面") // 日志输出
        showInvitedUsersScreen = false
    }

    /**
     * 打开购买记录页面
     */
    fun openPurchaseRecord() {
        println("HomeViewModel - 打开购买记录页面") // 日志输出
        showPurchaseRecordScreen = true
    }

    /**
     * 关闭购买记录页面
     */
    fun closePurchaseRecord() {
        println("HomeViewModel - 关闭购买记录页面") // 日志输出
        showPurchaseRecordScreen = false
    }

    /**
     * 打开提现页面
     */
    fun openWithdraw() {
        println("HomeViewModel - 打开提现页面") // 日志输出
        showWithdrawScreen = true
    }

    /**
     * 关闭提现页面
     */
    fun closeWithdraw() {
        println("HomeViewModel - 关闭提现页面") // 日志输出
        showWithdrawScreen = false
    }

    /**
     * 打开充值页面
     */
    fun openRecharge() {
        println("HomeViewModel - 打开充值页面") // 日志输出
        showRechargeScreen = true
    }

    /**
     * 关闭充值页面
     */
    fun closeRecharge() {
        println("HomeViewModel - 关闭充值页面") // 日志输出
        showRechargeScreen = false
    }

    /**
     * 打开添加银行卡页面
     */
    fun openAddBankCard() {
        println("HomeViewModel - 打开添加银行卡页面") // 日志输出
        showAddBankCardScreen = true
    }

    /**
     * 关闭添加银行卡页面
     */
    fun closeAddBankCard() {
        println("HomeViewModel - 关闭添加银行卡页面") // 日志输出
        showAddBankCardScreen = false
    }

    /**
     * 打开提现记录页面
     */
    fun openWithdrawRecord() {
        println("HomeViewModel - 打开提现记录页面") // 日志输出
        showWithdrawRecordScreen = true
    }

    /**
     * 关闭提现记录页面
     */
    fun closeWithdrawRecord() {
        println("HomeViewModel - 关闭提现记录页面") // 日志输出
        showWithdrawRecordScreen = false
    }

    /**
     * 打开收益记录页面
     */
    fun openEarningsRecord() {
        println("HomeViewModel - 打开收益记录页面") // 日志输出
        showEarningsRecordScreen = true
    }

    /**
     * 关闭收益记录页面
     */
    fun closeEarningsRecord() {
        println("HomeViewModel - 关闭收益记录页面") // 日志输出
        showEarningsRecordScreen = false
    }

    /**
     * 打开排行榜页面
     */
    fun openRanking() {
        println("HomeViewModel - 打开排行榜页面") // 日志输出
        showRankingScreen = true
    }

    /**
     * 关闭排行榜页面
     */
    fun closeRanking() {
        println("HomeViewModel - 关闭排行榜页面") // 日志输出
        showRankingScreen = false
    }

    /**
     * 打开关于页面
     */
    fun openAbout() {
        println("HomeViewModel - 打开关于页面") // 日志输出
        showAboutScreen = true
    }

    /**
     * 关闭关于页面
     */
    fun closeAbout() {
        println("HomeViewModel - 关闭关于页面") // 日志输出
        showAboutScreen = false
    }

    /**
     * 打开上传商品页面
     */
    fun openUploadProduct() {
        println("HomeViewModel - 打开上传商品页面") // 日志输出
        showUploadProductScreen = true
    }

    /**
     * 关闭上传商品页面
     */
    fun closeUploadProduct() {
        println("HomeViewModel - 关闭上传商品页面") // 日志输出
        showUploadProductScreen = false
    }
    
    /**
     * 打开升级管理员弹框
     */
    fun openUpgradeAdminDialog() {
        println("HomeViewModel - 打开升级管理员弹框") // 日志输出
        showUpgradeAdminDialog = true
    }
    
    /**
     * 关闭升级管理员弹框
     */
    fun closeUpgradeAdminDialog() {
        println("HomeViewModel - 关闭升级管理员弹框") // 日志输出
        showUpgradeAdminDialog = false
    }
    
    /**
     * 升级管理员 - 创建支付订单
     */
    fun upgradeToAdmin() {
        scope.launch {
            println("HomeViewModel - 开始创建升级管理员订单")
            
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("HomeViewModel - Token为空，无法创建订单")
                // 关闭升级弹框，显示登录弹框
                showUpgradeAdminDialog = false
                showLoginDialog = true
                return@launch
            }
            
            try {
                val result = repository.upgradeToAdmin(token)
                
                if (result.isSuccess) {
                    val response = result.getOrNull()
                    println("HomeViewModel - 升级管理员订单响应: ${response?.code}, ${response?.message}")
                    
                    // 检查是否为 401 错误（Token 失效）
                    if (response?.code == 401) {
                        println("HomeViewModel - Token 失效，显示登录弹框")
                        // 关闭升级弹框，显示登录弹框
                        showUpgradeAdminDialog = false
                        showLoginDialog = true
                        // 清除失效的 Token
                        TokenManager.clearToken()
                        isLoggedIn = false
                        currentToken = null
                        currentUser = null
                        currentUserRole = null
                        return@launch
                    }
                    
                    // 兼容易支付格式：code=1 或 code=200 都表示成功
                    if (response?.code == 1 || response?.code == 200) {
                        // 获取支付信息（优先从顶层字段获取，兼容旧格式）
                        val payurl = response.payurl ?: response.data?.payurl
                        val qrcodeImageUrl = response.img ?: response.data?.img
                        val orderNo = response.orderNo ?: response.data?.orderNo ?: response.trade_no
                        
                        println("HomeViewModel - payurl: $payurl")
                        println("HomeViewModel - img: $qrcodeImageUrl")
                        println("HomeViewModel - orderNo: $orderNo")
                        
                        upgradeAdminOrderNo = orderNo
                        
                        // 判断平台
                        val platform = getPlatform().name
                        println("HomeViewModel - 当前平台: $platform")
                        
                        if ((platform.contains("Android", ignoreCase = true) ||
                                    platform.contains("iOS", ignoreCase = true)) && payurl != null
                        ) {
                            // Android/iOS平台：直接调起支付宝APP
                            println("HomeViewModel - ${platform}平台，直接打开支付宝APP: $payurl")
                            UrlLauncher.openUrl(payurl)
                            
                            // 关闭升级弹框
                            showUpgradeAdminDialog = false
                            
                            // 启动后台轮询检查支付状态
                            if (orderNo != null) {
                                startUpgradeAdminPaymentPolling(orderNo, token)
                            }
                        } else if (qrcodeImageUrl != null) {
                            // Web平台：显示二维码弹窗
                            println("HomeViewModel - Web平台，显示二维码弹窗")
                            
                            // Web平台使用代理接口避免CORS
                            val proxiedQrcodeUrl = "https://shop.aiotzq.com/api/payment/proxy-image?url=$qrcodeImageUrl"
                            upgradeAdminQrcodeUrl = proxiedQrcodeUrl
                            
                            println("HomeViewModel - 升级管理员订单创建成功")
                            println("HomeViewModel - 订单号: ${upgradeAdminOrderNo}")
                            println("HomeViewModel - 二维码URL: $proxiedQrcodeUrl")
                            
                            // 关闭升级弹框，显示支付二维码弹框
                            showUpgradeAdminDialog = false
                            showUpgradeAdminQrcodeDialog = true
                        } else {
                            println("HomeViewModel - 未获取到支付信息")
                        }
                    } else {
                        println("HomeViewModel - 升级管理员订单创建失败: ${response?.message}")
                    }
                } else {
                    println("HomeViewModel - 升级管理员请求失败: ${result.exceptionOrNull()?.message}")
                }
            } catch (e: Exception) {
                println("HomeViewModel - 升级管理员异常: ${e.message}")
                e.printStackTrace()
            }
        }
    }
    
    /**
     * 启动升级管理员支付状态轮询
     */
    private fun startUpgradeAdminPaymentPolling(orderNo: String, token: String) {
        scope.launch {
            try {
                println("HomeViewModel - 启动升级管理员支付状态轮询，订单号: $orderNo")
                // 等待5秒后开始检查
                delay(5000)
                
                var retryCount = 0
                val maxRetries = 10
                var paymentVerified = false
                
                while (retryCount < maxRetries && !paymentVerified) {
                    if (retryCount > 0) {
                        println("HomeViewModel - 第${retryCount + 1}次查询升级支付状态，等待3秒...")
                        delay(3000)
                    }
                    
                    println("HomeViewModel - 检查升级支付状态，订单号: $orderNo (第${retryCount + 1}次)检测是否通知了")
                    val checkResponse = repository.checkUpgradeStatus(token)
                    
                    if (checkResponse.isSuccess) {
                        val result = checkResponse.getOrNull()
                        if (result != null && (result["code"] as? Int) == 200) {
                            val data = result["data"] as? Map<*, *>
                            val isUpgraded = data?.get("isUpgraded") as? Boolean ?: false
                            val currentRole = data?.get("currentRole") as? String ?: "user"
                            
                            println("HomeViewModel - 当前角色状态: $currentRole, 是否已升级: $isUpgraded")
                            
                            if (isUpgraded) {
                                println("HomeViewModel - 🎉 升级支付成功！您已成为管理员")
                                paymentVerified = true
                                
                                // 更新当前用户角色
                                currentUserRole = currentRole
                                
                                // 刷新首页数据（会重新加载用户信息）
                                refresh()
                            } else {
                                retryCount++
                            }
                        } else {
                            val message = result?.get("message") as? String ?: "未知错误"
                            println("HomeViewModel - 查询升级状态失败: $message")
                            retryCount++
                        }
                    } else {
                        println("HomeViewModel - 网络请求失败: ${checkResponse.exceptionOrNull()?.message}")
                        retryCount++
                    }
                }
                
                if (!paymentVerified) {
                    println("HomeViewModel - 未检测到支付，请手动刷新")
                }
            } catch (e: Exception) {
                println("HomeViewModel - 升级支付状态检查异常: ${e.message}")
                e.printStackTrace()
            }
        }
    }
    
    /**
     * 关闭升级管理员支付二维码弹框
     */
    fun closeUpgradeAdminQrcodeDialog() {
        println("HomeViewModel - 关闭升级管理员支付二维码弹框")
        showUpgradeAdminQrcodeDialog = false
        upgradeAdminQrcodeUrl = null
        upgradeAdminOrderNo = null
    }
    
    /**
     * 打开支付成功页面
     * @param orderNo 订单号
     * @param amount 支付金额
     * @param paymentMethod 支付方式（balance=余额, alipay=支付宝）
     * @param resourceUrl 资源链接（网盘地址，只有已支付订单才有值）
     */
    fun openPaymentSuccess(orderNo: String, amount: Double, paymentMethod: String = "balance", resourceUrl: String? = null) {
        println("HomeViewModel - 打开支付成功页面: orderNo=$orderNo, amount=$amount, paymentMethod=$paymentMethod, resourceUrl=$resourceUrl") // 日志输出
        paymentSuccessOrderNo = if (orderNo.isEmpty()) null else orderNo
        paymentSuccessAmount = if (amount == 0.0) null else amount
        paymentSuccessPaymentMethod = if (paymentMethod.isEmpty()) null else paymentMethod
        paymentSuccessResourceUrl = resourceUrl
        showPaymentSuccess = true
    }

    /**
     * 关闭支付成功页面，返回首页
     */
    fun closePaymentSuccess() {
        println("HomeViewModel - 关闭支付成功页面，返回首页") // 日志输出
        showPaymentSuccess = false
        showOrderDetail = false  // 同时关闭订单详情页
        paymentSuccessOrderNo = null
        paymentSuccessAmount = null
        paymentSuccessPaymentMethod = null
        paymentSuccessResourceUrl = null
        currentOrderNo = null
    }

    /**
     * 发送验证码
     */
    fun sendVerificationCode(phone: String, onSuccess: () -> Unit, onError: (String) -> Unit) {
        scope.launch {
            println("HomeViewModel - 请求发送验证码: $phone") // 日志输出

            val result = repository.sendCode(phone)

            if (result.isFailure) {
                val error = "发送失败: ${result.exceptionOrNull()?.message}"
                println("HomeViewModel - $error") // 日志输出
                onError(error)
                return@launch
            }

            val response = result.getOrNull()

            if (response?.code != 200) {
                val error = "发送失败: ${response?.message ?: "未知错误"}"
                println("HomeViewModel - $error") // 日志输出
                onError(error)
                return@launch
            }

            println("HomeViewModel - 验证码发送成功: ${response.message}") // 日志输出
            onSuccess()
        }
    }

    /**
     * 执行注册
     */
    fun performRegister(phone: String, password: String, code: String, invitationCode: String?) {
        scope.launch {
            println("HomeViewModel - 开始注册，手机号: $phone, 验证码: $code, 邀请码: ${invitationCode ?: "无"}") // 日志输出
            uiState = uiState.copy(isLoading = true, error = null)

            // 获取设备信息用于注册
            val deviceId = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceId()
            } catch (e: Exception) {
                println("HomeViewModel - 获取设备ID失败: ${e.message}")
                val error = "获取设备信息失败，无法注册"
                uiState = uiState.copy(isLoading = false, error = error)
                return@launch
            }
            
            val deviceName = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceName()
            } catch (e: Exception) {
                null
            }
            
            val deviceType = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceType()
            } catch (e: Exception) {
                null
            }
            
            println("HomeViewModel - 设备信息: deviceId=$deviceId, deviceName=$deviceName, deviceType=$deviceType")

            val registerResult = repository.register(phone, password, code, invitationCode, deviceId, deviceName, deviceType)

            if (registerResult.isFailure) {
                val error = "注册失败: ${registerResult.exceptionOrNull()?.message}"
                println("HomeViewModel - $error") // 日志输出
                uiState = uiState.copy(isLoading = false, error = error)
                return@launch
            }

            val registerResponse = registerResult.getOrNull()

            // 检查注册是否成功
            if (registerResponse?.code != 200) {
                val error = "注册失败: ${registerResponse?.message ?: "未知错误"}"
                println("HomeViewModel - $error") // 日志输出
                uiState = uiState.copy(isLoading = false, error = error)
                return@launch
            }

            println("HomeViewModel - 注册成功：${registerResponse.message}，注册设备已自动授权") // 日志输出
            println("HomeViewModel - 用户ID: ${registerResponse.data?.userId}") // 日志输出

            // 清除待注册的邀请码（注册成功后不再需要）
            TokenManager.clearPendingInvitationCode()
            println("HomeViewModel - 已清除待注册邀请码")

            // 注册成功后自动登录（使用相同的设备信息）
            println("HomeViewModel - 注册成功，自动登录...") // 日志输出
            performLogin(phone, password)
        }
    }

    /**
     * 执行登录
     */
    fun performLogin(username: String, password: String) {
        scope.launch {
            println("HomeViewModel - 开始登录，手机号: $username") // 日志输出
            uiState = uiState.copy(isLoading = true, error = null)
    
            // 获取设备信息
            val deviceId = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceId()
            } catch (e: Exception) {
                println("HomeViewModel - 获取设备ID失败: ${e.message}")
                val error = "获取设备信息失败，无法登录"
                uiState = uiState.copy(isLoading = false, error = error)
                return@launch
            }
                
            val deviceName = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceName()
            } catch (e: Exception) {
                null
            }
                
            val deviceType = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceType()
            } catch (e: Exception) {
                null
            }
                
            println("HomeViewModel - 设备信息: deviceId=$deviceId, deviceName=$deviceName, deviceType=$deviceType")
    
            val loginResult = repository.login(username, password, deviceId, deviceName, deviceType)
    
            if (loginResult.isFailure) {
                val error = "登录失败: ${loginResult.exceptionOrNull()?.message}"
                println("HomeViewModel - $error") // 日志输出
                uiState = uiState.copy(isLoading = false, error = error)
                return@launch
            }
    
            val loginResponse = loginResult.getOrNull()
    
            // 检查登录是否成功
            if (loginResponse?.code != 200 || loginResponse.data?.token == null) {
                // 特殊处理403错误（设备未授权）
                val error = if (loginResponse?.code == 403) {
                    "⚠️ 设备未授权\n\n此设备尚未获得登录授权，无法登录。\n\n设备信息：\n• 设备ID: ${deviceId.take(16)}...\n• 设备名称: ${deviceName ?: "未知"}\n• 设备类型: ${deviceType ?: "未知"}\n\n请联系管理员授权此设备后再登录。"
                } else {
                    "登录失败: ${loginResponse?.message ?: "未知错误"}"
                }
                println("HomeViewModel - $error") // 日志输出
                uiState = uiState.copy(isLoading = false, error = error)
                return@launch
            }
    
            val token = loginResponse.data.token
            val user = loginResponse.data?.user?.phoneNumber ?: username
    
            println("HomeViewModel - 登录成功，Token: $token, 用户: $user, 余额: ${loginResponse.data?.user?.balance}") // 日志输出
    
            // 清除待注册的邀请码（登录成功后不再需要）
            TokenManager.clearPendingInvitationCode()
            println("HomeViewModel - 已清除待注册邀请码")
    
            // 保存登录状态
            isLoggedIn = true
            currentToken = token
            currentUser = user
            showLoginDialog = false
            
            // 清空错误信息
            uiState = uiState.copy(error = null)
                
            // 获取用户角色
            getUserRole(token)
    
            // 登录成功提示（数据已经加载过了，无需重新加载）
            println("HomeViewModel - 登录完成，首页数据已存在，无需重新加载") // 日志输出
            uiState = uiState.copy(isLoading = false)
        }
    }


    /**
     * 加载Banner数据（无需Token）
     */
    private fun loadBanners() {
        scope.launch {
            println("HomeViewModel - 开始加载Banner数据（无需登录）") // 日志输出
            val result = repository.getHomeBanners("")

            if (result.isSuccess) {
                val banners = result.getOrNull()?.data ?: emptyList()
                println("HomeViewModel - Banner加载成功，数量: ${banners.size}") // 日志输出
                uiState = uiState.copy(banners = banners)
            } else {
                val error = "加载Banner失败: ${result.exceptionOrNull()?.message}"
                println("HomeViewModel - $error") // 日志输出
            }
        }
    }

    /**
     * 加载分类数据（无需Token）
     */
    private fun loadCategories() {
        scope.launch {
            println("HomeViewModel - 开始加载分类数据（无需登录）") // 日志输出
            val result = repository.getCategories("")

            if (result.isSuccess) {
                val categories = result.getOrNull()?.data ?: emptyList()
                println("HomeViewModel - 分类加载成功，数量: ${categories.size}") // 日志输出
                uiState = uiState.copy(categories = categories)
            } else {
                val error = "加载分类失败: ${result.exceptionOrNull()?.message}"
                println("HomeViewModel - $error") // 日志输出
            }
        }
    }

    /**
     * 加载商品列表（无需Token）
     * @param categoryId 分类ID（null表示获取全部商品）
     * @param page 页码（默认1）
     * @param pageSize 每页数量（默认10）
     */
    private fun loadProducts(
        categoryId: String? = null,
        page: String = "1",
        pageSize: String = "10"
    ) {
        scope.launch {
            println("HomeViewModel - 开始加载商品数据（无需登录）, CategoryId: $categoryId, Page: $page, PageSize: $pageSize") // 日志输出

            val result = if (categoryId != null) {
                // 根据分类ID筛选商品（支持分页）
                println("HomeViewModel - 调用分类商品接口: categoryId=$categoryId") // 日志输出
                repository.getProductsByCategory(
                    token = "",
                    categoryId = categoryId,
                    page = page,
                    pageSize = pageSize
                )
            } else {
                // 获取全部商品列表（支持分页）
                println("HomeViewModel - 调用全部商品接口") // 日志输出
                repository.getProducts(
                    token = "",
                    page = page,
                    pageSize = pageSize
                )
            }

            if (result.isSuccess) {
                val products = result.getOrNull()?.data ?: emptyList()
                println("HomeViewModel - 商品加载成功，数量: ${products.size}") // 日志输出

                // 所有商品都显示在“为你推荐”中
                println("HomeViewModel - 将所有商品设置为推荐商品") // 日志输出

                uiState = uiState.copy(
                    hotProducts = emptyList(),  // 不再使用热门商品
                    recommendProducts = products,  // 所有商品都作为推荐
                    isLoading = false
                )
            } else {
                val error = "加载商品失败: ${result.exceptionOrNull()?.message}"
                println("HomeViewModel - $error") // 日志输出
                uiState = uiState.copy(isLoading = false, error = error)
            }
        }
    }

    /**
     * 加载首页数据（无需登录）
     */
    private fun loadHomeData() {
        println("HomeViewModel - 开始加载首页数据（无需Token）") // 日志输出
        loadBanners()
        loadCategories()
        loadProducts()
    }

    /**
     * 选择分类（无需登录）
     * - 点击“全部”按钮：显示全部商品列表（GET /products?page=1&pageSize=10）
     * - 点击其他分类：根据分类ID筛选商品（GET /products/category?categoryId=xxx&page=1&pageSize=10）
     */
    fun selectCategory(categoryId: Int?) {
        println("=".repeat(60)) // 分隔线
        println("HomeViewModel - 选择分类: ${categoryId ?: "全部"}") // 日志输出

        selectedCategoryId = categoryId

        scope.launch {
            uiState = uiState.copy(isLoading = true)

            if (categoryId == null) {
                // 点击“全部”按钮 - 获取全部商品列表
                println("HomeViewModel - 加载全部商品列表 (page=1, pageSize=10)") // 日志输出
                loadProducts(categoryId = null, page = "1", pageSize = "10")
            } else {
                // 点击其他分类 - 根据分类ID筛选
                println("HomeViewModel - 加载分类商品 (categoryId=$categoryId, page=1, pageSize=10)") // 日志输出
                loadProducts(categoryId = categoryId.toString(), page = "1", pageSize = "10")
            }
        }

        println("=".repeat(60)) // 分隔线
    }

    /**
     * 下拉刷新
     * 根据当前选中的分类刷新数据
     */
    fun refresh() {
        if (isRefreshing) return
        
        scope.launch {
            println("HomeViewModel - 开始下拉刷新")
            println("HomeViewModel - 当前选中的分类: ${selectedCategoryId ?: "全部"}")
            isRefreshing = true
            currentPage = 1
            hasMore = true
            
            // 根据当前选中的分类刷新数据
            if (selectedCategoryId == null) {
                // 刷新全部商品
                println("HomeViewModel - 刷新全部商品")
                loadHomeData()
            } else {
                // 刷新当前分类的商品
                println("HomeViewModel - 刷新分类商品 (categoryId=$selectedCategoryId)")
                loadProducts(categoryId = selectedCategoryId.toString(), page = "1", pageSize = "10")
            }
            
            // 模拟网络延迟，确保刷新动画可见
            delay(500)
            isRefreshing = false
            println("HomeViewModel - 刷新完成")
        }
    }
    
    /**
     * 加载更多数据（上拉加载）
     * 实现循环分页加载：加载完后从第1页重新开始
     */
    fun loadMore() {
        if (isLoadingMore) {
            println("HomeViewModel - 跳过加载更多：正在加载中")
            return
        }
        
        scope.launch {
            println("HomeViewModel - 开始上拉加载更多，当前页：$currentPage")
            isLoadingMore = true
            
            // 加载下一页
            val nextPage = currentPage + 1
            println("HomeViewModel - 请求第 $nextPage 页数据")
            
            val result = if (selectedCategoryId != null) {
                // 加载分类商品的下一页（每页10条）
                repository.getProductsByCategory(
                    token = "",
                    categoryId = selectedCategoryId.toString(),
                    page = nextPage.toString(),
                    pageSize = "10"
                )
            } else {
                // 加载全部商品的下一页（每页10条）
                repository.getProducts(
                    token = "",
                    page = nextPage.toString(),
                    pageSize = "10"
                )
            }
            
            if (result.isSuccess) {
                val newProducts = result.getOrNull()?.data ?: emptyList()
                println("HomeViewModel - 第 $nextPage 页加载成功，新增商品数量: ${newProducts.size}")
                
                if (newProducts.isEmpty()) {
                    // 没有更多数据了，从第1页重新开始循环加载
                    println("HomeViewModel - 没有更多数据了，从第1页重新开始循环加载")
                    println("HomeViewModel - 【当前页码: $currentPage | 总商品数: ${uiState.recommendProducts.size}】")
                    
                    // 重新加载第1页
                    val firstPageResult = if (selectedCategoryId != null) {
                        repository.getProductsByCategory(
                            token = "",
                            categoryId = selectedCategoryId.toString(),
                            page = "1",
                            pageSize = "10"
                        )
                    } else {
                        repository.getProducts(
                            token = "",
                            page = "1",
                            pageSize = "10"
                        )
                    }
                    
                    if (firstPageResult.isSuccess) {
                        val firstPageProducts = firstPageResult.getOrNull()?.data ?: emptyList()
                        println("HomeViewModel - 循环加载：第1页加载成功，新增商品数量: ${firstPageProducts.size}")
                        
                        if (firstPageProducts.isNotEmpty()) {
                            // 追加第1页数据
                            val currentProducts = uiState.recommendProducts
                            val updatedProducts = currentProducts + firstPageProducts
                            
                            uiState = uiState.copy(
                                recommendProducts = updatedProducts
                            )
                            
                            // 重置页码为1，继续循环
                            currentPage = 1
                            hasMore = true
                            println("HomeViewModel - 【✓ 循环加载成功 | 重新从第 1 页开始 | 总商品数: ${updatedProducts.size}】")
                        }
                    }
                } else {
                    // 追加到现有列表
                    val currentProducts = uiState.recommendProducts
                    val updatedProducts = currentProducts + newProducts
                    
                    uiState = uiState.copy(
                        recommendProducts = updatedProducts
                    )
                    
                    // 更新页码
                    currentPage = nextPage
                    println("HomeViewModel - 当前总商品数: ${updatedProducts.size}，当前页码: $currentPage")
                    println("HomeViewModel - 【✓ 加载成功 | 第 $currentPage 页 | 总商品数: ${updatedProducts.size}】")
                }
            } else {
                val error = "加载更多失败: ${result.exceptionOrNull()?.message}"
                println("HomeViewModel - $error")
            }
            
            isLoadingMore = false
            println("HomeViewModel - 加载更多完成")
        }
    }

    /**
     * 清理资源
     */
    fun onCleared() {
        networkMonitor.stopMonitoring()
        repository.close()
    }
    
    /**
     * 关闭网络状态对话框
     */
    fun dismissNetworkDialog() {
        showNetworkDialog = false
    }

    /**
     * 打开资源信息页面
     */
    fun openResourceInfo(product: Product) {
        println("HomeViewModel - 打开资源信息页面: ${product.name}, 百度网盘链接: ${product.baiduUrl}")
        _resourceProduct.value = product
        showResourceInfo = true
    }

    /**
     * 关闭资源信息页面
     */
    fun closeResourceInfo() {
        println("HomeViewModel - 关闭资源信息页面")
        showResourceInfo = false
        _resourceProduct.value = null
    }
    
    /**
     * 获取商品详情用于资源信息页面
     */
    fun fetchProductDetailForResource(productId: Int, onResult: (Product?) -> Unit) {
        scope.launch {
            try {
                println("HomeViewModel - 开始获取商品详情用于资源信息页面，商品ID: $productId") // 日志输出
                val productDetailResponse = repository.getProductDetail(productId.toString())
                
                if (productDetailResponse.isSuccess) {
                    val productDetail = productDetailResponse.getOrNull()?.data
                    if (productDetail != null) {
                        // 将 ProductDetail 转换为 Product
                        // 注意：百度网盘链接存储在 image3 字段中
                        val product = Product(
                            id = productDetail.id,
                            name = productDetail.name,
                            price = productDetail.price,
                            category = productDetail.category,
                            imageUrl = productDetail.imageUrl,
                            sales = productDetail.sales,
                            views = productDetail.views,
                            description = productDetail.description,
                            status = productDetail.status,
                            createdAt = productDetail.createdAt,
                            updatedAt = productDetail.updatedAt,
                            baiduUrl = productDetail.image3,  // 从 image3 字段获取百度网盘链接
                            extractCode = productDetail.extractCode,
                            otherResources = productDetail.otherResources
                        )
                        println("HomeViewModel - 商品详情转换成功，百度网盘链接: ${product.baiduUrl}") // 日志输出
                        onResult(product)
                    } else {
                        println("HomeViewModel - 无法获取商品详情，商品可能不存在，商品ID: $productId") // 日志输出
                        onResult(null)
                    }
                } else {
                    println("HomeViewModel - 获取商品详情失败，商品ID: $productId, 错误: ${productDetailResponse.exceptionOrNull()?.message}") // 日志输出
                    onResult(null)
                }
            } catch (e: Exception) {
                println("HomeViewModel - 获取商品详情异常，商品ID: $productId, 错误: ${e.message}") // 日志输出
                e.printStackTrace()
                onResult(null)
            }
        }
    }
}

/**
 * 首页UI状态
 */
data class HomeUiState(
    val isLoading: Boolean = false,
    val error: String? = null,
    val banners: List<Banner> = emptyList(),
    val categories: List<Category> = emptyList(),
    val hotProducts: List<Product> = emptyList(),
    val recommendProducts: List<Product> = emptyList()
)
